'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import KittingProgressRing from '@/app/components/KittingProgressRing';
import KittingComponentList from '@/app/components/KittingComponentList';
import KittingScanBar from '@/app/components/KittingScanBar';
import KittingFlashOverlay from '@/app/components/KittingFlashOverlay';
import KittingCompleteModal from '@/app/components/KittingCompleteModal';
import KittingCurrentPrompt from '@/app/components/KittingCurrentPrompt';
import { toast } from 'sonner';

export interface KitComponent {
  id: string;
  itemCode: string;
  itemName: string;
  requiredQty: number;
  scannedQty: number;
  uom: string;
  batchSuggested: string;
  batchScanned: string | null;
  expiryDate: string;
  barcode: string;
  status: 'pending' | 'inprogress' | 'complete';
  location: string;
}

const initialComponents: KitComponent[] = [
  {
    id: 'kc-001',
    itemCode: 'ITM-BAN-001',
    itemName: 'Elastic Bandage 4"',
    requiredQty: 4,
    scannedQty: 4,
    uom: 'PCS',
    batchSuggested: 'BAT-BAN-2024A',
    batchScanned: 'BAT-BAN-2024A',
    expiryDate: '31-Dec-2027',
    barcode: 'ITM-BAN-001|BAT-BAN-2024A|31122027|00001',
    status: 'complete',
    location: 'A-02-01-B-03',
  },
  {
    id: 'kc-002',
    itemCode: 'ITM-GAU-002',
    itemName: 'Sterile Gauze Pad 4x4"',
    requiredQty: 6,
    scannedQty: 3,
    uom: 'PCS',
    batchSuggested: 'BAT-GAU-2025B',
    batchScanned: 'BAT-GAU-2025B',
    expiryDate: '15-Mar-2028',
    barcode: 'ITM-GAU-002|BAT-GAU-2025B|15032028|00001',
    status: 'inprogress',
    location: 'A-02-01-C-07',
  },
  {
    id: 'kc-003',
    itemCode: 'ITM-SYR-003',
    itemName: 'Disposable Syringe 5ml',
    requiredQty: 3,
    scannedQty: 0,
    uom: 'PCS',
    batchSuggested: 'BAT-SYR-2025C',
    batchScanned: null,
    expiryDate: '30-Jun-2027',
    barcode: 'ITM-SYR-003|BAT-SYR-2025C|30062027|00001',
    status: 'pending',
    location: 'B-01-03-A-02',
  },
  {
    id: 'kc-004',
    itemCode: 'ITM-GVS-004',
    itemName: 'Nitrile Gloves (Pair) L',
    requiredQty: 2,
    scannedQty: 0,
    uom: 'PR',
    batchSuggested: 'BAT-GVS-2025D',
    batchScanned: null,
    expiryDate: '01-Sep-2028',
    barcode: 'ITM-GVS-004|BAT-GVS-2025D|01092028|00001',
    status: 'pending',
    location: 'B-01-03-B-04',
  },
  {
    id: 'kc-005',
    itemCode: 'ITM-ANT-005',
    itemName: 'Antiseptic Wipes (Pack)',
    requiredQty: 2,
    scannedQty: 0,
    uom: 'PCK',
    batchSuggested: 'BAT-ANT-2024E',
    batchScanned: null,
    expiryDate: '14-Nov-2026',
    barcode: 'ITM-ANT-005|BAT-ANT-2024E|14112026|00001',
    status: 'pending',
    location: 'C-03-02-A-01',
  },
  {
    id: 'kc-006',
    itemCode: 'ITM-TRN-006',
    itemName: 'Tourniquet — Combat',
    requiredQty: 1,
    scannedQty: 0,
    uom: 'PCS',
    batchSuggested: 'BAT-TRN-2025F',
    batchScanned: null,
    expiryDate: '20-Aug-2029',
    barcode: 'ITM-TRN-006|BAT-TRN-2025F|20082029|00001',
    status: 'pending',
    location: 'C-03-02-B-06',
  },
  {
    id: 'kc-007',
    itemCode: 'ITM-SCM-007',
    itemName: 'Trauma Scissors',
    requiredQty: 1,
    scannedQty: 0,
    uom: 'PCS',
    batchSuggested: 'BAT-SCM-2025G',
    batchScanned: null,
    expiryDate: '31-Dec-2030',
    barcode: 'ITM-SCM-007|BAT-SCM-2025G|31122030|00001',
    status: 'pending',
    location: 'C-03-02-C-09',
  },
];

type FlashState = 'idle' | 'correct' | 'wrong';

export default function KittingWorkspace() {
  const [components, setComponents] = useState<KitComponent[]>(initialComponents);
  const [flashState, setFlashState] = useState<FlashState>('idle');
  const [flashMessage, setFlashMessage] = useState('');
  const [flashSub, setFlashSub] = useState('');
  const [showComplete, setShowComplete] = useState(false);
  const [scanHistory, setScanHistory] = useState<{ time: string; item: string; result: 'ok' | 'err' }[]>([]);
  const flashTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeIndex = components.findIndex(
    (c) => c.status === 'inprogress' || c.status === 'pending'
  );
  const activeComponent = activeIndex !== -1 ? components[activeIndex] : null;

  const totalRequired = components.reduce((s, c) => s + c.requiredQty, 0);
  const totalScanned = components.reduce((s, c) => s + c.scannedQty, 0);
  const completionPct = totalRequired > 0 ? Math.round((totalScanned / totalRequired) * 100) : 0;

  const triggerFlash = useCallback(
    (state: 'correct' | 'wrong', msg: string, sub: string) => {
      if (flashTimeout.current) clearTimeout(flashTimeout.current);
      setFlashState(state);
      setFlashMessage(msg);
      setFlashSub(sub);
      flashTimeout.current = setTimeout(() => {
        setFlashState('idle');
      }, 1400);
    },
    []
  );

  const handleScan = useCallback(
    (barcode: string) => {
      const now = new Date();
      const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

      if (!activeComponent) {
        triggerFlash('wrong', 'ALL ITEMS COMPLETE', 'No more items to scan');
        return;
      }

      const trimmed = barcode.trim();
      // Accept full barcode or just item code prefix
      const isCorrect =
        trimmed === activeComponent.barcode ||
        trimmed === activeComponent.itemCode ||
        trimmed.startsWith(activeComponent.itemCode);

      if (isCorrect) {
        setScanHistory((prev) => [
          { time: timeStr, item: activeComponent.itemName, result: 'ok' },
          ...prev.slice(0, 19),
        ]);

        setComponents((prev) => {
          const updated = [...prev];
          const comp = { ...updated[activeIndex] };
          comp.scannedQty = comp.scannedQty + 1;
          if (comp.scannedQty >= comp.requiredQty) {
            comp.status = 'complete';
            comp.batchScanned = comp.batchSuggested;
            // Check if all done
            const allDone = updated.every((c, i) =>
              i === activeIndex ? comp.scannedQty >= comp.requiredQty : c.status === 'complete'
            );
            updated[activeIndex] = comp;
            if (allDone) {
              setTimeout(() => setShowComplete(true), 600);
              triggerFlash('correct', 'KIT COMPLETE!', 'All items verified — generating kit barcode');
              return updated;
            }
            // Activate next pending
            const nextIdx = updated.findIndex((c, i) => i > activeIndex && c.status === 'pending');
            if (nextIdx !== -1) {
              updated[nextIdx] = { ...updated[nextIdx], status: 'inprogress' };
            }
          } else {
            comp.status = 'inprogress';
            comp.batchScanned = comp.batchSuggested;
          }
          updated[activeIndex] = comp;
          const remaining = comp.requiredQty - comp.scannedQty;
          triggerFlash(
            'correct',
            'CORRECT ITEM',
            comp.scannedQty >= comp.requiredQty
              ? `${comp.itemName} — Complete`
              : `${comp.itemName} — ${remaining} more needed`
          );
          return updated;
        });
      } else {
        setScanHistory((prev) => [
          { time: timeStr, item: barcode, result: 'err' },
          ...prev.slice(0, 19),
        ]);
        triggerFlash(
          'wrong',
          'WRONG ITEM SCANNED',
          `Expected: ${activeComponent.itemName} (${activeComponent.itemCode})`
        );
        toast.error(`Wrong scan: expected ${activeComponent.itemCode}`, {
          description: activeComponent.itemName,
          duration: 3000,
        });
      }
    },
    [activeComponent, activeIndex, triggerFlash]
  );

  useEffect(() => {
    return () => {
      if (flashTimeout.current) clearTimeout(flashTimeout.current);
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Flash Overlay */}
      <KittingFlashOverlay state={flashState} message={flashMessage} sub={flashSub} />

      {/* Complete Modal */}
      {showComplete && (
        <KittingCompleteModal
          components={components}
          onClose={() => setShowComplete(false)}
        />
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-6 py-5">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            {/* Left: Progress + Current Prompt */}
            <div className="xl:col-span-1 flex flex-col gap-4">
              <KittingProgressRing
                completionPct={completionPct}
                totalScanned={totalScanned}
                totalRequired={totalRequired}
                completedItems={components.filter((c) => c.status === 'complete').length}
                totalItems={components.length}
              />
              {activeComponent && (
                <KittingCurrentPrompt component={activeComponent} />
              )}
              {/* Scan History */}
              <div className="card-elevated p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Recent Scans
                </p>
                {scanHistory.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">No scans yet</p>
                ) : (
                  <ul className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-thin">
                    {scanHistory.map((s, i) => (
                      <li
                        key={`scan-${i}-${s.time}`}
                        className="flex items-center gap-2 text-xs fade-in"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            s.result === 'ok' ? 'bg-primary' : 'bg-red-500'
                          }`}
                        />
                        <span className="font-mono-data text-muted-foreground w-16 flex-shrink-0">
                          {s.time}
                        </span>
                        <span
                          className={`truncate ${
                            s.result === 'ok' ? 'text-foreground' : 'text-red-400'
                          }`}
                        >
                          {s.item}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Right: Component list */}
            <div className="xl:col-span-2">
              <KittingComponentList
                components={components}
                activeIndex={activeIndex}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scan Bar — always at bottom */}
      <KittingScanBar onScan={handleScan} activeComponent={activeComponent} />
    </div>
  );
}